import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMugHot, faPlus, faXmark, faCheck } from '@fortawesome/free-solid-svg-icons';
import { cn } from '../../../../ui/utils/cn';
import { Input, Button } from '../../../../ui';
import { getBrandTypes } from '../../api/coffeeApi';
import { SupermarketFormat, SupermarketType } from '../../CoffeeCardWeb/types';

// What the picker emits upward: the selected product type. An existing catalog type
// carries a `key`; a user-defined one has `key: undefined` (created-or-get pending at
// submit time via typeName + typeFormat). Mirrors the supermarket create body.
interface TypePickerProps {
    // an approved brand's id or slug — used to fetch its approved types. A brand-new
    // brand (no ref) has no types yet, so only the add-a-type path is offered.
    brandIdOrSlug?: string;
    value: SupermarketType | null;
    onChange: (next: SupermarketType | null) => void;
    disabled?: boolean;
}

// the three format toggles a user picks for a custom type
const FORMAT_TOGGLES: { key: keyof SupermarketFormat; label: string; emoji: string }[] = [
    { key: 'milk', label: 'milk', emoji: '🥛' },
    { key: 'sugar', label: 'sugar', emoji: '🍬' },
    { key: 'iced', label: 'iced', emoji: '🧊' },
];

// small milk/sugar/iced hint chips shown on each type
const FormatHints: React.FC<{ format: SupermarketFormat }> = ({ format }) => {
    const hints = FORMAT_TOGGLES.filter((t) => format[t.key]);
    if (hints.length === 0) {
        return <span className="text-[10px] text-muted-foreground">black</span>;
    }
    return (
        <span className="flex items-center gap-1">
            {hints.map((h) => (
                <span key={h.key} className="text-[10px]" title={h.label}>
                    {h.emoji}
                </span>
            ))}
        </span>
    );
};

const TypePicker: React.FC<TypePickerProps> = ({ brandIdOrSlug, value, onChange, disabled = false }) => {
    const [types, setTypes] = useState<SupermarketType[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    // inline add-a-type mini-form
    const [adding, setAdding] = useState(false);
    const [customName, setCustomName] = useState('');
    const [customFormat, setCustomFormat] = useState<SupermarketFormat>({ milk: false, sugar: false, iced: false });

    // fetch the brand's approved types whenever the brand changes. A brand without a
    // ref/slug (just-created pending) has none — skip the call and offer add-a-type only.
    useEffect(() => {
        if (!brandIdOrSlug) {
            setTypes([]);
            return;
        }
        let cancelled = false;
        setIsLoading(true);
        getBrandTypes(brandIdOrSlug)
            .then((list) => {
                if (!cancelled) setTypes(list);
            })
            .catch(() => {
                if (!cancelled) setTypes([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [brandIdOrSlug]);

    const selectExisting = (type: SupermarketType) => {
        onChange(type);
        setAdding(false);
    };

    const toggleCustomFormat = (key: keyof SupermarketFormat) => {
        setCustomFormat((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const confirmCustom = () => {
        const name = customName.trim();
        if (!name) return;
        // a custom type has no catalog key — it's created-or-get pending at submit time.
        onChange({ key: '', name, format: { ...customFormat } });
        setAdding(false);
        setCustomName('');
        setCustomFormat({ milk: false, sugar: false, iced: false });
    };

    const clearSelection = () => {
        onChange(null);
    };

    // selected state: a removable pill, no list/add UI until cleared.
    if (value) {
        return (
            <div className="flex items-center justify-between gap-2 p-3 border border-mint/50 bg-mint/10 rounded-lg">
                <div className="flex items-center gap-2 min-w-0">
                    <FontAwesomeIcon icon={faMugHot} className="h-3.5 w-3.5 text-mint shrink-0" />
                    <span className="font-medium text-sm truncate">{value.name}</span>
                    <FormatHints format={value.format} />
                    {!value.key && (
                        <span className="text-xs bg-mint/20 text-mint px-1.5 py-0.5 rounded shrink-0">new</span>
                    )}
                </div>
                {!disabled && (
                    <button
                        type="button"
                        onClick={clearSelection}
                        className="text-muted-foreground hover:text-ink transition-colors shrink-0"
                        aria-label="Clear selected type"
                    >
                        <FontAwesomeIcon icon={faXmark} className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-2">
            {isLoading && (
                <div className="px-2 py-1.5 text-xs text-muted-foreground">loading types…</div>
            )}

            {!isLoading && types.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {types.map((type) => (
                        <button
                            key={type.key}
                            type="button"
                            disabled={disabled}
                            onClick={() => selectExisting(type)}
                            className={cn(
                                'flex items-center gap-2 px-3 py-2 border border-ink/10 bg-background rounded-lg cursor-pointer transition-all',
                                'hover:shadow-md hover:border-mint/40 hover:bg-mint/5',
                                disabled && 'opacity-60 cursor-not-allowed',
                            )}
                        >
                            <span className="font-medium text-sm">{type.name}</span>
                            <FormatHints format={type.format} />
                        </button>
                    ))}
                </div>
            )}

            {!isLoading && types.length === 0 && (
                <p className="text-xs text-muted-foreground px-1">
                    no types yet — add the one you had below.
                </p>
            )}

            {/* add-a-type inline mini-form */}
            {adding ? (
                <div className="space-y-3 border border-ink/10 bg-muted/10 rounded-xl p-3">
                    <Input
                        placeholder="Type name (e.g. Caffè Latte)"
                        value={customName}
                        disabled={disabled}
                        onChange={(e) => setCustomName(e.target.value)}
                        className="h-9 text-sm"
                    />
                    <div className="flex flex-wrap gap-2">
                        {FORMAT_TOGGLES.map((t) => (
                            <button
                                key={t.key}
                                type="button"
                                disabled={disabled}
                                onClick={() => toggleCustomFormat(t.key)}
                                className={cn(
                                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all border-2',
                                    customFormat[t.key]
                                        ? 'border-mint bg-mint/10 text-foreground shadow-sm'
                                        : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300',
                                )}
                            >
                                <span>{t.emoji}</span>
                                <span className="lowercase">{t.label}?</span>
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center justify-end gap-2">
                        <Button
                            type="button"
                            variant="soft"
                            size="sm"
                            onClick={() => {
                                setAdding(false);
                                setCustomName('');
                            }}
                            disabled={disabled}
                            className="px-3"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant={customName.trim() ? 'softPrimary' : 'soft'}
                            size="sm"
                            onClick={confirmCustom}
                            disabled={disabled || !customName.trim()}
                            icon={faCheck}
                            iconPosition="left"
                            className="px-3"
                        >
                            Use type
                        </Button>
                    </div>
                </div>
            ) : (
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => setAdding(true)}
                    className={cn(
                        'w-full p-2.5 border border-dashed border-ink/20 bg-muted/10 rounded-lg cursor-pointer transition-all',
                        'hover:border-mint/40 hover:bg-mint/5 flex items-center justify-center gap-2',
                        disabled && 'opacity-60 cursor-not-allowed',
                    )}
                >
                    <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm font-medium text-muted-foreground">Add a type</span>
                </button>
            )}
        </div>
    );
};

export default TypePicker;
